import { Keyboard } from 'lucide-react';
import type { ReactNode } from 'react';
import { Kbd } from '../../../components/Kbd';
import styles from './ranking.module.css';

// RNK-01 §10 and F23.14 (ADR 0049, as in the mockup): the keys, always in sight under the list, so they are
// found rather than remembered. Click a row (or arrow into the list) and they act on that row.

const KEYS: readonly { readonly keys: string; readonly label: string; readonly isDecision?: true }[] = [
  { keys: '↑ ↓', label: 'Move' },
  { keys: 'Space', label: 'Select' },
  { keys: 'M', label: 'Remark' },
  { keys: 'A', label: 'Shortlist', isDecision: true },
  { keys: 'H', label: 'Maybe', isDecision: true },
  { keys: 'R', label: 'Reject', isDecision: true },
  { keys: 'Enter', label: 'Open CV' },
  // Redesign Group 3: in the open sheet, the person before or after in the list (↑/↓ scroll the CV there).
  { keys: '[ ]', label: 'Previous / next CV' },
  { keys: '/', label: 'Search names' },
  { keys: '1–4', label: 'Tabs' },
];

interface KeyHintsProps {
  readonly canDecide: boolean;
  readonly canRemark: boolean;
  readonly isJevOn?: boolean;
  /** ADR 0062: `I` opens the ideal candidate (edits it, or sets it in job setup) for anyone who may edit it, with Ask Jev on or off. */
  readonly canEditProfile?: boolean;
  /** ADR 0062: `F` focuses on a criterion, a key that exists only while Screening criteria is on. */
  readonly isCriteriaOn?: boolean;
}

export function KeyHints({ canDecide, canRemark, isJevOn = false, canEditProfile = false, isCriteriaOn = false }: KeyHintsProps): ReactNode {
  // ADR 0050: with Jev on, `/` goes to the Ask Jev box, as in the mockup.
  const base = KEYS.filter((key) => (key.isDecision ? canDecide : key.keys === 'M' ? canRemark : true)).map((key) => (isJevOn && key.keys === '/' ? { ...key, label: 'Ask Jev' } : key));
  const shown = [...base, ...(canEditProfile ? [{ keys: 'I', label: 'Ideal candidate' }] : []), ...(isCriteriaOn ? [{ keys: 'F', label: 'Focus on a criterion' }] : [])];
  return (
    <div className={styles.keyHints} aria-label="Keyboard keys">
      <Keyboard size={16} aria-hidden />
      {shown.map((key) => (
        <span key={key.keys} className={styles.keyHint}>
          <Kbd>{key.keys}</Kbd>
          {key.label}
        </span>
      ))}
    </div>
  );
}
